import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Loader, Typography } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../hooks';
import { useLazyGetUserDetailsQuery } from '../../store/globalApis';
import { type SodSidePanelUserData } from '../../types';
import { createDevRejectionHandler } from '../../utils';
import SodFieldSet from '../SodFieldSet';
import SodUserCard from '../SodUserCard';

import './SodSidePanelUser.css';

interface SodSidePanelUserProps {
  usernameQuery: string;
  isInSidePanel?: boolean;
}

/**
 * SodSidePanelUser
 */
function SodSidePanelUser({
  usernameQuery,
  isInSidePanel = false,
}: SodSidePanelUserProps) {
  const l = useInvertedLocalize();
  const abortRef = useRef<((reason: string) => void) | null>(null);

  const [data, setData] = useState<SodSidePanelUserData | null>(null);
  const [isUserFound, setIsUserFound] = useState<boolean>(false);

  const [fetchUserDetials, { isLoading, isError, isFetching }] =
    useLazyGetUserDetailsQuery();

  const handFetchUserDetails = useCallback(
    async (payload: string) => {
      abortRef.current?.('New Search');

      try {
        const controller = fetchUserDetials(payload);

        abortRef.current = () => controller.abort();

        const response = await controller.unwrap();

        return response;
      } catch (error: unknown) {
        throw new Error(error instanceof Error ? error.message : String(error));
      }
    },
    [fetchUserDetials]
  );

  useEffect(() => {
    handFetchUserDetails(usernameQuery)
      .then((response) => {
        const typedResponse = response as SodSidePanelUserData;

        setData(typedResponse);
      })
      .catch((error) => {
        const errorStr = error instanceof Error ? error.message : String(error);

        if (errorStr.includes('412')) {
          setIsUserFound(true);
        }
      });
  }, [usernameQuery, handFetchUserDetails]);

  if (isLoading || isFetching) {
    return (
      <Box className='Sod-SidePanelUser-loaderWrapper'>
        <Loader kind='dots' format='inline' color='brand' />
      </Box>
    );
  }

  if (isUserFound || isError) {
    return (
      <Box className='Sod-SidePanelUser-loaderWrapper'>
        <Typography kind='body2'>
          {l(
            isUserFound
              ? 'User Not Found.'
              : 'Error loading User details, please try again.'
          )}
        </Typography>
        {!isUserFound && (
          <Button
            type='button'
            kind='outlined'
            isEnabled
            onClick={() => {
              handFetchUserDetails(usernameQuery)
                .then((response) => {
                  const typedResponse = response as SodSidePanelUserData;

                  setData(typedResponse);
                })
                .catch(
                  createDevRejectionHandler(
                    'SodSidePanelUser: handFetchUserDetails'
                  )
                );
            }}>
            {l('Retry')}
          </Button>
        )}
      </Box>
    );
  }

  if (data === null) {
    return null;
  }

  // if the api is returning array it means empty username is passed in payload
  // and api return all data in return
  if (Array.isArray(data)) {
    return (
      <Box className='Sod-SidePanelSummary-loaderWrapper'>
        <Typography kind='body2'>{l('No Records Found.')}</Typography>
      </Box>
    );
  }

  const {
    firstname,
    lastname,
    displayname,
    username,
    systemUserName,
    email,
    employeeid,
    country,
    city,
    employeetype,
    employeeclass,
    manager,
    jobDescription,
    location,
    companyname,
    costcenter,
    region,
    entity,
    orgunitid,
    identitytype,
    userstatus,
  } = data;

  const tabDetails = isInSidePanel
    ? 'Sod-SidePanelUser-tabDetails--sidePanel'
    : 'Sod-SidePanelUser-tabDetails';

  return (
    <Box className={tabDetails}>
      <SodUserCard
        firstName={firstname}
        lastName={lastname}
        userName={username}
        className='Sod-SidePanelUser-card'
      />
      <Box className='Sod-SidePanelUser-trustDetails'>
        <Typography kind='h4'>{l('Trust')}</Typography>
        <Box>
          <Typography kind='h3'>{l('Trust Block Coming Soon...')}</Typography>
        </Box>
      </Box>

      <Box className='Sod-SidePanelUser-otherDetails'>
        {username && <SodFieldSet label={l('Username')} value={username} />}
        {systemUserName && (
          <SodFieldSet label={l('System Username')} value={systemUserName} />
        )}
        {firstname && <SodFieldSet label={l('First Name')} value={firstname} />}
        {lastname && <SodFieldSet label={l('Last Name')} value={lastname} />}
        {displayname && (
          <SodFieldSet label={l('Display Name')} value={displayname} />
        )}
        {employeeid && (
          <SodFieldSet label={l('Employee ID')} value={employeeid} />
        )}
        {identitytype && (
          <SodFieldSet label={l('Identity Type')} value={identitytype} />
        )}
        {employeetype && (
          <SodFieldSet label={l('Employee Type')} value={employeetype} />
        )}
        {userstatus && (
          <SodFieldSet label={l('User Status')} value={l(userstatus)} />
        )}

        {email && <SodFieldSet label={l('Email')} value={email} />}
        {employeeclass && (
          <SodFieldSet label={l('Employee Class')} value={l(employeeclass)} />
        )}
        {jobDescription && (
          <SodFieldSet
            label={l('Job Description')}
            value={jobDescription}
            className='Sod-SidePanelUser-otherDetails--span-both'
          />
        )}

        {manager && (
          <SodFieldSet
            label={l('Manager')}
            value={
              <SodUserCard
                firstName={' '.trim()}
                lastName={' '.trim()}
                userName={manager}
                className='Sod-DetailsUserManager'
              />
            }
            className='Sod-SidePanelUser-otherDetails--span-both'
          />
        )}

        {city && <SodFieldSet label={l('City')} value={city} />}
        {country && <SodFieldSet label={l('Country')} value={country} />}
        {location && <SodFieldSet label={l('Location')} value={location} />}
        {companyname && (
          <SodFieldSet label={l('Company Name')} value={companyname} />
        )}
        {costcenter && (
          <SodFieldSet label={l('Cost Center')} value={costcenter} />
        )}
        {region && <SodFieldSet label={l('Region')} value={region} />}
        {entity && <SodFieldSet label={l('Entity')} value={entity} />}
        {orgunitid && (
          <SodFieldSet label={l('Org Unit ID')} value={orgunitid} />
        )}
      </Box>
    </Box>
  );
}

SodSidePanelUser.defaultProps = {
  isInSidePanel: false,
};

export default SodSidePanelUser;
